import { GetCurrentUserDocument } from '~/client/graph/operations/generated/Users.generated'
import Config from '~/client/src/shared/Config'
import { UrlParamKey } from '~/client/src/shared/constants/commonRoutes'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import CommonStore from '~/client/src/shared/stores/ui/Common.store'

import IUserSession from '../../interfaces/IUserSession'
import ApiAuthService, { AuthMeResponse } from '../../services/ApiAuthService'
import StruxHubAuthService from '../../services/StruxHubAuthService'
import { stripMobileSuffix } from '../../utils/authPortalHosts'
import { getTokenPayload } from '../../utils/JwtHelper'
import { isLoginBundle } from '../../utils/loginPortal'
import { INIT_AUTH_USER } from '../EventStore/eventConstants'
import EventsStore from '../EventStore/Events.store'
import InitialState from '../InitialState'
import TenantsStore, { ITenant } from './Tenants.store'

export default class AuthenticationStore {
  private _isFromSSO: boolean = false
  private _hasPassword: boolean | null = null
  // Backend-derived flags from GET /auth/me. Authoritative for UI decisions
  // because they reflect the user's account record + tenant policy regardless
  // of which login path the user came through. Populated on every auth-state
  // change. null means the call hasn't completed (or failed).
  private _hasEmail: boolean | null = null
  private _isSSOEnforced: boolean | null = null
  private _isPasswordRequired: boolean | null = null

  /**
   * Whether the user has a password provider linked. Populated by
   * loadAuthMe() which calls /auth/me. null means the check hasn't
   * completed (or failed).
   */
  public get hasPassword(): boolean | null {
    return this._hasPassword
  }

  /**
   * Whether the user's account record has an email identifier
   * (STX, tenant user, or verified Firebase JWT email claim).
   * Required for any password flow.
   */
  public get hasEmail(): boolean | null {
    return this._hasEmail
  }

  /**
   * True when password is the user's only available credential — no tenant
   * they belong to allows SSO for their domain. Drives the auto-redirect
   * to /save-password. False for hybrid / SSO-only tenants where password
   * is optional. null while /auth/me is in flight.
   */
  public get isPasswordRequired(): boolean | null {
    return this._isPasswordRequired
  }

  public async loadAuthMe(): Promise<void> {
    try {
      const accessToken = await this.struxHubAuthService.getValidAccessToken()
      const me: AuthMeResponse = await this.apiService.getAuthMe(accessToken)
      this._hasPassword = me.hasPassword
      this._hasEmail = me.hasEmail
      this._isSSOEnforced = me.isSSOEnforced
      this._isPasswordRequired = me.isPasswordRequired
    } catch (error) {
      console.error('Failed to load /auth/me', error)
      // null signals failure — guards default to "don't render password UI"
      this._hasPassword = null
      this._hasEmail = null
      this._isSSOEnforced = null
      this._isPasswordRequired = null
    }
  }

  public get isAuthenticated(): boolean {
    return this.struxHubAuthService.isAuthenticated
  }

  /**
   * True when password UI should be hidden for this user — i.e., the tenant
   * SSO policy blocks password sign-in.
   *
   * Primary source: backend `/auth/me` response (`_isSSOEnforced`). It's
   * authoritative and durable across reloads because /auth/me runs on every
   * auth-state change. While that call is in-flight on a fresh SSO login,
   * the synchronous `_isFromSSO` flag (set by SSOCallbackView) and the STX
   * JWT `auth_method` claim cover the gap.
   */
  public get isFromSSO(): boolean {
    // Backend-derived signal wins when available (STX1-419). It reflects the
    // user's tenant policy, not just the path of the current session.
    if (this._isSSOEnforced !== null) {
      return this._isSSOEnforced
    }

    if (this._isFromSSO) {
      return true
    }

    if (this.struxHubAuthService.isFromSSO) {
      return true
    }

    // Fallback: check initialState session directly
    const token = this.initialState.tenantUserSession?.accessToken
    if (token) {
      try {
        const payload = getTokenPayload(token)
        if (payload?.auth_method === 'sso') {
          return true
        }
      } catch {
        // Ignore token parsing errors
      }
    }

    return false
  }

  public setIsFromSSO(value: boolean): void {
    this._isFromSSO = value
  }

  public get authEmail(): string {
    return (
      this.struxHubAuthService.currentUser?.email ||
      this.initialState.tenantUserSession?.email
    )
  }

  public constructor(
    private readonly commonStore: CommonStore,
    private readonly struxHubAuthService: StruxHubAuthService,
    private readonly apiService: ApiAuthService,
    private readonly eventsStore: EventsStore,
    private readonly initialState: InitialState,
    private readonly tenantsStore: TenantsStore,
  ) {}

  /**
   * Returns a function that can be used to get a valid access token.
   * @param noLogout Use to prevent nesting when the caller is the LOGOUT event handler itself.
   */
  public getValidAccessTokenCallback(
    noLogout?: boolean,
  ): () => Promise<string> {
    try {
      return this.struxHubAuthService.getValidAccessToken
    } catch (error) {
      console.error(error)
      if (!noLogout) {
        this.eventsStore.dispatch(e.LOGOUT)
      }
    }
  }

  public async getTenantsForUser(accessToken: string): Promise<Array<ITenant>> {
    return this.apiService.getTenantsForUser(accessToken)
  }

  // --- Login methods (all unified through STX Auth API) ---

  /**
   * Login with email and password. Always uses STX Auth API (no tenanted/tenantless split).
   */
  public async login(email: string, password: string) {
    const session = await this.struxHubAuthService.loginWithEmailPassword(
      email,
      password,
    )
    this.initialState.tenantUserSession = session

    const tenants = await this.apiService.getTenantsForUser(session.accessToken)
    if (!tenants?.length) {
      throw new Error('No tenants found')
    }

    this.tenantsStore.setTenants(tenants)

    // Always dispatch INIT_AUTH_USER to continue the auth flow.
    // initAuthUser() handles both single-tenant (auto-select → INIT_APP)
    // and multi-tenant (portal on /projects).
    this.eventsStore.dispatch(INIT_AUTH_USER)
  }

  /**
   * Login with phone OTP via STX Auth API.
   */
  public async loginWithPhoneOTP(
    phone: string,
    oneTimeCode: string,
    nonsense: string,
  ) {
    const session = await this.struxHubAuthService.loginWithPhoneOTP(
      phone,
      oneTimeCode,
      nonsense,
    )
    this.initialState.tenantUserSession = session

    const tenants = await this.apiService.getTenantsForUser(session.accessToken)
    if (!tenants?.length) {
      throw new Error('No tenants found')
    }

    this.tenantsStore.setTenants(tenants)
    this.eventsStore.dispatch(INIT_AUTH_USER)
  }

  /**
   * Login using invite key. Uses STX JWT from the response.
   *
   * If a `tenantHost` URL parameter is present (mobile WebView in a preview
   * environment whose hostname does not match the active tenant), set
   * tenantHostOverride from it so subsequent tenant resolution uses the
   * intended tenant rather than the preview hostname.
   */
  public async loginWithInviteKey(inviteKey: string) {
    const urlTenantHost = new URLSearchParams(window.location.search).get(
      UrlParamKey.TenantHost,
    )

    // Local-dev: the LAN-IP/localhost origin is not a tenant host, so the
    // backend cannot resolve the tenant from the request Origin. Forward
    // the real tenant host (carried on ?tenantHost by the Redirector) so
    // the invite-key exchange resolves it. Production resolves via Origin.
    const requestTenantHost =
      process.env.NODE_ENV !== 'production' ? urlTenantHost : undefined

    const session = await this.struxHubAuthService.loginWithInviteKey(
      inviteKey,
      requestTenantHost,
    )
    this.adoptTokenLoginSession(session, urlTenantHost)
  }

  /**
   * Login with a single-use session handoff code (STX1-495). Like the invite
   * key, honours a `tenantHost` URL param (mobile WebView on a preview host).
   */
  public async loginWithHandoffCode(code: string) {
    const session = await this.struxHubAuthService.loginWithHandoffCode(code)
    this.adoptTokenLoginSession(
      session,
      new URLSearchParams(window.location.search).get(UrlParamKey.TenantHost),
    )
  }

  // A ?tenantHost from the URL (mobile WebView on a preview host) overrides
  // tenant resolution for a session adopted from a URL-token login.
  private adoptTokenLoginSession(
    session: IUserSession,
    urlTenantHost: string | null,
  ) {
    if (urlTenantHost) {
      this.struxHubAuthService.setTenantHostOverride(urlTenantHost)
      session.tenantHostOverride = urlTenantHost
    }
    this.initialState.tenantUserSession = session
  }

  /**
   * Complete SSO login using tokens from exchangeSSOCode().
   */
  public completeSSOLogin(accessToken: string, refreshToken: string) {
    const session = this.struxHubAuthService.loginWithTokens(
      accessToken,
      refreshToken,
    )
    this.initialState.tenantUserSession = session
  }

  // --- Password flows (delegated to Auth API) ---

  public async resetPassword(email: string) {
    return this.apiService.resetPassword(email)
  }

  public async verifyPasswordResetCode(resetCode: string): Promise<void> {
    return this.apiService.validateResetCode(resetCode)
  }

  public async confirmPasswordReset(
    resetCode: string,
    newPassword: string,
  ): Promise<void> {
    return this.apiService.confirmPasswordReset(resetCode, newPassword)
  }

  /**
   * Sets the account password. Changing an existing password requires the
   * current one (the auth service re-verifies it); a first-time set omits it.
   */
  public async setPassword(newPassword: string, currentPassword?: string) {
    const accessToken = await this.struxHubAuthService.getValidAccessToken()
    return this.apiService.setPassword(
      newPassword,
      accessToken,
      currentPassword,
    )
  }

  // --- Logout ---

  public logout() {
    this.struxHubAuthService.logout()
    this.initialState.tenantUserSession = null
  }

  // --- Auth initialization ---

  /**
   * Unified auth initialization (replaces initAuthUserTenantlessMode).
   * Checks for existing session, resolves tenant, creates graph client, dispatches INIT_APP.
   * Works for all modes — no tenanted/tenantless split.
   */
  public async initAuthUser() {
    try {
      // Try to restore session from localStorage if not already set
      let user = this.initialState.tenantUserSession
      if (!user) {
        user = this.struxHubAuthService.restoreSession()
        if (user) {
          this.initialState.tenantUserSession = user
        }
      }

      if (!user) {
        return this.eventsStore.dispatch(e.INIT_AUTH_USER_2)
      }

      this.initialState.onAuthStateChangedCallback?.()

      let tenants: ITenant[]
      if (this.tenantsStore.userTenants?.length) {
        tenants = this.tenantsStore.userTenants
      } else {
        tenants = await this.getTenantsForUser(user.accessToken)
      }

      if (!tenants?.length) {
        console.warn('No tenants available! Logging out')
        this.eventsStore.dispatch(e.LOGOUT)
        return
      }

      this.tenantsStore.setTenants(tenants)

      if (tenants.length === 1) {
        this.tenantsStore.setActiveTenant(tenants[0].host)
        user.tenantHostOverride = tenants[0].host
        this.struxHubAuthService.setTenantHostOverride(tenants[0].host)
      } else if (!user.tenantHostOverride && !isLoginBundle()) {
        // Non-tenantless: auto-select tenant matching current hostname.
        // Preview envs have hostnames like pr-xxx.dev.struxsite.com
        // which contain the tenant host (dev.struxsite.com).
        // Mobile twins (<tenant>-mobile.<domain>) collapse to their
        // canonical tenant host first — shared stripMobileSuffix, same
        // canonicalization Config and the login portal use. Falls back
        // to Config.TENANT_ID name match for localhost dev, where the
        // hostname doesn't carry tenant info but the bundle's
        // /configuration pins which tenant it should act as.
        const hostname = stripMobileSuffix(location.hostname)
        const tenantId = Config.TENANT_ID
        const match = tenants.find(
          t =>
            hostname === t.host ||
            hostname.endsWith('.' + t.host) ||
            (!!tenantId && t.name === tenantId),
        )
        if (match) {
          this.tenantsStore.setActiveTenant(match.host)
          user.tenantHostOverride = match.host
          this.struxHubAuthService.setTenantHostOverride(match.host)
        }
      }

      if (!user.tenantHostOverride || isLoginBundle()) {
        // Two cases land here, both meaning "don't boot into a tenant now":
        //  1. Multi-tenant without a selection — the User Portal handles it.
        //  2. The login bundle, always. The portal is its landing and tenant
        //     selection is per-navigation (return_to → Redirector), never a
        //     persisted boot target. A persisted tenantHostOverride there is
        //     stale (left by a prior enter-project); honoring it would run the
        //     tenant-init INIT_APP chain, which is a no-op on the transportless
        //     login graphClient and ends up bouncing the user to /login
        //     (project_relogin_stale_activeproject). The portal renders and the
        //     user picks a tenant; return_to fly-over is driven by
        //     TenantRoutingView, not this chain.
        this.initialState.loading.set(e.INIT_AUTH_USER, false)
        return
      }

      // graphClient is created once at container init with location.host;
      // the auth callback resolves the current token per-request so the same
      // client instance works pre- and post-login. Recreating it here used to
      // orphan React-Apollo hook subscriptions (responses delivered to the
      // new client's listeners; subscribers on the previous instance stayed
      // in `loading: true` indefinitely).
      this.tenantsStore.setActiveTenant(user.tenantHostOverride)

      // Verify tenant access before full init.
      // Catches password users on SSO-only tenants (middleware returns 401).
      // Skipped on the login bundle: its graphClient is intentionally
      // transportless (uri: null) since the login app has no tenant Graph
      // to talk to — the access check belongs on the tenant subdomain
      // post-redirect, where the graphClient has a real URL. Running the
      // query here on the login bundle would always throw and bounce the
      // user back to LOGOUT after every email/password or phone-OTP login.
      if (!isLoginBundle()) {
        try {
          await this.initialState.graphClient.query({
            query: GetCurrentUserDocument,
            fetchPolicy: 'network-only',
          })
        } catch (accessError) {
          if (this.isProviderBlockedError(accessError)) {
            console.warn(
              `Sign-in method not allowed for tenant '${user.tenantHostOverride}'. ` +
                'This tenant may require SSO login.',
            )
            user.tenantHostOverride = null
            this.eventsStore.dispatch(e.LOGOUT)
            return
          }
          throw accessError
        }
      }

      // Load /auth/me so password-policy flags (hasPassword, isPasswordRequired,
      // isSSOEnforced, hasEmail) are populated before INIT_APP fans out. On
      // develop this fired from the Firebase onAuthStateChanged callback in
      // observeAuthState(); that hook was removed with the Firebase Auth
      // scaffolding and never rewired into the new STX session flow, so
      // password UI gates were stuck on `null` (short-circuit "don't render").
      await this.loadAuthMe()

      return this.eventsStore.dispatch(e.INIT_APP)
    } catch (error) {
      console.error('An error occurred during auth initialization', error)
      this.eventsStore.dispatch(e.LOGOUT)
    }
  }

  public async resendInviteKey(expiredInviteKey: string): Promise<void> {
    return this.apiService.resendInviteKey(expiredInviteKey)
  }

  // --- Helpers ---

  /**
   * Check if an Apollo error is a provider-blocked 401 from the SSO middleware.
   * The middleware returns plain text like "Sign-in method 'password' is not allowed..."
   */
  private isProviderBlockedError(error: any): boolean {
    const networkError = error?.networkError
    if (!networkError || networkError.statusCode !== 401) {
      return false
    }
    const body =
      networkError.bodyText ||
      (typeof networkError.result === 'string' ? networkError.result : '')
    return typeof body === 'string' && body.includes('not allowed')
  }
}
